/**
 * completion.ts
 *
 * Single source of truth for a project's completion percentage.
 *
 * The stored `Project.CompletionPercent` column is only authoritative for
 * projects tracked manually (fixed-price % complete) or when a PM has set an
 * explicit override. For everything else the figure is derived from hours so
 * the Projects list, its Excel export, and the project Overview card all show
 * the same live value.
 */

import type { Project } from '@/types';

/** Hours inputs, allowing callers to supply enriched ResourceBudget totals. */
export interface CompletionHoursOverrides {
  budgetHours?: number | null;
  actualHours?: number | null;
}

/**
 * Auto-calculated % from actual hours ÷ budget hours, falling back to the
 * stored value when there is no hours budget to measure against.
 */
export function computeAutoCalculatedCompletionPercent(
  project: Pick<Project, 'budgetHours' | 'actualHours' | 'completionPercent'>,
  hours?: CompletionHoursOverrides,
): number {
  const budgetHours = hours?.budgetHours ?? project.budgetHours ?? 0;
  const actualHours = hours?.actualHours ?? project.actualHours ?? 0;
  return budgetHours > 0
    ? Math.round((actualHours / budgetHours) * 100)
    : project.completionPercent ?? 0;
}

/**
 * The effective completion %, respecting the PM override and calculation mode.
 * When mode is 'based-on-time' (the default) the value is derived from hours
 * rather than reading the stale Project.CompletionPercent column.
 */
export function computeEffectiveCompletionPercent(
  project: Pick<
    Project,
    'budgetHours' | 'actualHours' | 'completionPercent' | 'completionPercentMode' | 'completionPercentOverride'
  >,
  hours?: CompletionHoursOverrides,
): number {
  if (project.completionPercentOverride != null) return project.completionPercentOverride;
  if (project.completionPercentMode === 'manual') return project.completionPercent ?? 0;
  return computeAutoCalculatedCompletionPercent(project, hours);
}
